import { c as _c } from "react/compiler-runtime";
import { deleteOrgCredentialsByCredentialIdMutation, getOrgCredentialsQueryKey } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { IcoDotsHorizontal16, IcoKey16 } from '@onefootprint/icons';
import type { AgentCredential, AgentResponse, OrganizationMember } from '@onefootprint/request-types/dashboard';
import { Badge, Card, Dropdown, IconButton, InlineAlert, Popover, Shimmer, useToast } from '@onefootprint/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hostOf } from '@/components/agent-chat/utils/host-of';
import type { CredentialsScope } from '../../types';
type CredentialsListProps = {
  credentials?: AgentCredential[];
  scope: CredentialsScope;
  isLoading?: boolean;
  errorMessage?: string;
};
const CredentialsList = t0 => {
  const $ = _c(13);
  const {
    credentials,
    scope,
    isLoading,
    errorMessage
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "credentials"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  let t2;
  if ($[1] !== scope.kind || $[2] !== t) {
    t2 = scope.kind === "agent" ? t(_temp) : t(_temp2);
    $[1] = scope.kind;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const emptyStateText = t2;
  let t3;
  if ($[4] !== credentials || $[5] !== emptyStateText || $[6] !== errorMessage || $[7] !== isLoading || $[8] !== scope || $[9] !== t) {
    t3 = () => {
      if (isLoading) {
        return <div role="status" aria-label={t(_temp3)} className="flex flex-col gap-1">{[0, 1].map(_temp4)}</div>;
      }
      if (errorMessage) {
        return <InlineAlert variant="error">{errorMessage}</InlineAlert>;
      }
      if (!credentials?.length) {
        return <Card.Empty>{emptyStateText}</Card.Empty>;
      }
      return <Card.List aria-label={t(_temp5)}>{credentials.map(credential => <Row key={credential.id} credential={credential} scope={scope} />)}</Card.List>;
    };
    $[4] = credentials;
    $[5] = emptyStateText;
    $[6] = errorMessage;
    $[7] = isLoading;
    $[8] = scope;
    $[9] = t;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const renderContent = t3;
  let t4;
  if ($[11] !== renderContent) {
    t4 = <Card.Root>{renderContent()}</Card.Root>;
    $[11] = renderContent;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
};
type RowProps = {
  credential: AgentCredential;
  scope: CredentialsScope;
};
const Row = t0 => {
  const $ = _c(41);
  const {
    credential,
    scope
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "credentials"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    provider
  } = credential;
  const isMine = scope.kind === "user" || credential.tenantUserId === scope.currentUserId;
  let t2;
  if ($[1] !== provider.allowedOrigins || $[2] !== provider.items || $[3] !== provider.kind || $[4] !== provider.username || $[5] !== t) {
    t2 = () => {
      if (provider.kind === "one_password") {
        if (provider.items.length === 0) {
          return t(_temp6);
        }
        if (provider.items.length === 1) {
          return t(_temp7, {
            count: 1
          });
        }
        return t(_temp8, {
          count: provider.items.length
        });
      }
      const sites = provider.allowedOrigins.length === 0 ? t(_temp9) : provider.allowedOrigins.map(hostOf).join(", ");
      return t(_temp10, {
        sites,
        username: provider.username
      });
    };
    $[1] = provider.allowedOrigins;
    $[2] = provider.items;
    $[3] = provider.kind;
    $[4] = provider.username;
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const access = t2;
  const t3 = credential.label;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span aria-hidden={true} className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-tertiary bg-secondary text-secondary"><IcoKey16 /></span>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== credential.label) {
    t5 = <p className="text-label-3 text-primary break-words">{credential.label}</p>;
    $[8] = credential.label;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== provider.kind || $[11] !== t) {
    t6 = provider.kind === "one_password" ? t(_temp11) : t(_temp12);
    $[10] = provider.kind;
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <span className="text-body-3 text-tertiary">{t6}</span>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t7) {
    t8 = <div className="flex flex-wrap items-center gap-x-3 gap-y-1">{t5}{t7}</div>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== access) {
    t9 = access();
    $[18] = access;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = <span className="break-words">{t9}</span>;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== credential || $[23] !== isMine || $[24] !== scope.agents || $[25] !== scope.kind || $[26] !== scope.members) {
    t11 = scope.kind === "agent" ? <AddedBy credential={credential} isMine={isMine} members={scope.members} /> : <Agents credential={credential} agents={scope.agents} />;
    $[22] = credential;
    $[23] = isMine;
    $[24] = scope.agents;
    $[25] = scope.kind;
    $[26] = scope.members;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t10 || $[29] !== t11) {
    t12 = <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-body-3 text-tertiary">{t10}{t11}</div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t12 || $[32] !== t8) {
    t13 = <div className="min-w-0 flex-1">{t8}{t12}</div>;
    $[31] = t12;
    $[32] = t8;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== credential || $[35] !== isMine) {
    t14 = isMine && <RowMenu credential={credential} />;
    $[34] = credential;
    $[35] = isMine;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== credential.label || $[38] !== t13 || $[39] !== t14) {
    t15 = <Card.Body as="li" aria-label={t3} className="flex items-center gap-3 px-4 py-3">{t4}{t13}{t14}</Card.Body>;
    $[37] = credential.label;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  return t15;
};
type AgentsProps = {
  credential: AgentCredential;
  agents: AgentResponse[];
};
const Agents = t0 => {
  const $ = _c(12);
  const {
    credential,
    agents
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "credentials"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  if (credential.allowedAgentIds.length === 0) {
    let t2;
    if ($[1] !== t) {
      t2 = t(_temp13);
      $[1] = t;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== t2) {
      t3 = <span className="text-tertiary">{t2}</span>;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  let t2;
  if ($[5] !== agents || $[6] !== credential.allowedAgentIds || $[7] !== open || $[8] !== t) {
    let t3;
    if ($[10] !== agents) {
      t3 = id => agents.find(agent => agent.id === id)?.name ?? id;
      $[10] = agents;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    const names = credential.allowedAgentIds.map(t3);
    t2 = <div className="flex min-w-0 items-center gap-1"><Badge variant="neutral" className="min-w-0" title={names[0]}><span className="truncate">{names[0]}</span></Badge>{names.length > 1 && <Popover controlledOpen={open} onControlledOpenChange={setOpen} onEscapeKeyDown={() => setOpen(false)} side="bottom" align="end" content={<div className="w-64 p-3"><ul className="max-h-60 overflow-y-auto space-y-2 text-body-2 text-primary">{names.map((name, index) => <li key={credential.allowedAgentIds[index]} className="break-words whitespace-normal">{name}</li>)}</ul></div>}><button type="button" aria-label={t(_temp14, {
          count: names.length
        })} className="shrink-0 rounded-lg px-2 py-1 text-caption-1 text-primary bg-neutral hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">+{names.length - 1}</button></Popover>}</div>;
    $[5] = agents;
    $[6] = credential.allowedAgentIds;
    $[7] = open;
    $[8] = t;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
type AddedByProps = {
  credential: AgentCredential;
  isMine: boolean;
  members: OrganizationMember[];
};
const AddedBy = t0 => {
  const $ = _c(10);
  const {
    credential,
    isMine,
    members
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "credentials"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  let t2;
  if ($[1] !== credential || $[2] !== isMine || $[3] !== members || $[4] !== t) {
    let t3;
    if ($[6] !== credential) {
      t3 = candidate => candidate.id === credential.tenantUserId;
      $[6] = credential;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    const member = members.find(t3);
    const fullName = member ? [member.firstName, member.lastName].filter(Boolean).join(" ") : "";
    t2 = isMine ? t(_temp15) : member ? t(_temp16, {
      name: fullName || member.email
    }) : t(_temp17);
    $[1] = credential;
    $[2] = isMine;
    $[3] = members;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const label = t2;
  let t3;
  if ($[8] !== label) {
    t3 = <span>{label}</span>;
    $[8] = label;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
type RowMenuProps = {
  credential: AgentCredential;
};
const RowMenu = t0 => {
  const $ = _c(31);
  const {
    credential
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "credentials"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const queryClient = useQueryClient();
  const {
    toast
  } = useToast();
  const showRequestErrorToast = useRequestErrorToast();
  const sharedWithAgents = credential.allowedAgentIds.length > 0;
  const [open, setOpen] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = deleteOrgCredentialsByCredentialIdMutation();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== credential.label || $[3] !== queryClient || $[4] !== sharedWithAgents || $[5] !== t || $[6] !== toast) {
    t3 = async () => {
      await queryClient.invalidateQueries({
        queryKey: getOrgCredentialsQueryKey()
      });
      const title = sharedWithAgents ? t(_temp18, {
        name: credential.label
      }) : t(_temp19, {
        name: credential.label
      });
      toast({
        variant: "default",
        title,
        description: ""
      });
    };
    $[2] = credential.label;
    $[3] = queryClient;
    $[4] = sharedWithAgents;
    $[5] = t;
    $[6] = toast;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== showRequestErrorToast || $[9] !== t3) {
    t4 = {
      ...t2,
      onSuccess: t3,
      onError: showRequestErrorToast
    };
    $[8] = showRequestErrorToast;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const remove = useMutation(t4);
  let t5;
  if ($[11] !== credential.label || $[12] !== t) {
    t5 = t(_temp20, {
      name: credential.label
    });
    $[11] = credential.label;
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = open ? "bg-senary/70!" : undefined;
  let t7;
  if ($[14] !== remove.isPending || $[15] !== t5 || $[16] !== t6) {
    t7 = <Dropdown.Trigger asChild={true}><IconButton aria-label={t5} size="compact" icon={IcoDotsHorizontal16} className={t6} disabled={remove.isPending} /></Dropdown.Trigger>;
    $[14] = remove.isPending;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== credential.id || $[19] !== remove) {
    t8 = () => remove.mutate({
      path: {
        credentialId: credential.id
      }
    });
    $[18] = credential.id;
    $[19] = remove;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== sharedWithAgents || $[22] !== t) {
    t9 = sharedWithAgents ? t(_temp21) : t(_temp22);
    $[21] = sharedWithAgents;
    $[22] = t;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t8 || $[25] !== t9) {
    t10 = <Dropdown.Portal><Dropdown.Content align="end"><Dropdown.Group><Dropdown.Item destructive={true} onSelect={t8}>{t9}</Dropdown.Item></Dropdown.Group></Dropdown.Content></Dropdown.Portal>;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== open || $[28] !== t10 || $[29] !== t7) {
    t11 = <Dropdown.Root open={open} onOpenChange={setOpen}>{t7}{t10}</Dropdown.Root>;
    $[27] = open;
    $[28] = t10;
    $[29] = t7;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  return t11;
};
export default CredentialsList;
function _temp($) {
  return $.empty.agent;
}
function _temp2($_0) {
  return $_0.empty.user;
}
function _temp3($_1) {
  return $_1.loading;
}
function _temp4(index) {
  return <Card.Body key={index} className="px-4 py-3"><Shimmer height="40px" width="100%" /></Card.Body>;
}
function _temp5($_2) {
  return $_2.title;
}
function _temp6($) {
  return $.detail.anyVault;
}
function _temp7($_0) {
  return $_0.detail.scoped_one;
}
function _temp8($_1) {
  return $_1.detail.scoped_other;
}
function _temp9($_2) {
  return $_2.detail.anySite;
}
function _temp10($_3) {
  return $_3.detail.login;
}
function _temp11($_4) {
  return $_4.kind.onePassword;
}
function _temp12($_5) {
  return $_5.kind.password;
}
function _temp13($) {
  return $.table.chatOnly;
}
function _temp14($_0) {
  return $_0.table.showAgents;
}
function _temp15($) {
  return $.detail.addedByYou;
}
function _temp16($_0) {
  return $_0.detail.addedBy;
}
function _temp17($_1) {
  return $_1.detail.addedByTeammate;
}
function _temp18($) {
  return $.actions.removedEverywhere;
}
function _temp19($_0) {
  return $_0.actions.removed;
}
function _temp20($_1) {
  return $_1.actions.menuAriaLabel;
}
function _temp21($_2) {
  return $_2.actions.removeEverywhere;
}
function _temp22($_3) {
  return $_3.actions.remove;
}